import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MapboxGlMap, {
    Layer,
    MapboxEvent,
    MapLayerMouseEvent,
    MapRef,
    Marker,
    Source,
    TransformRequestFunction,
} from 'react-map-gl';
import MapPopup from './MapPopup';
import { useAppSelector } from '../../../types';
import {
    displayDegradationsQuery,
    displayPrQuery,
    displayRoadsQuery,
    highlightedRoadSectionLineQuery,
    initialBoundsQuery,
    networkMapQuery,
    selectedGradeReferenceQuery,
    selectedmapStyleQuery,
} from '../../../core/networkMap/queries';
import EnlargedImageModal from './EnlargedImageModal';
import ImagePopupOnClick from './MapPopup/ImagePopup/PopupOnClick';
import ImagePopupOnHover from './MapPopup/ImagePopup/PopupOnHover';
import useCreatePopupHover from './hooks/use-create-popup-hover';
import useCreatePopupClicked from './hooks/use-create-popup-clicked';
import { LAYER_NETWORK_DATA_ID, getLayerConfig } from './network-map-layer-configuration';
import {
    PR_LAYER,
    PR_LAYER_PAINT_SATELLITE,
    PR_LAYER_PAINT_STREET,
    PR_REUNION_LAYER,
    PR_REUNION_SOURCE_ID,
    PR_REUNION_SOURCE_URL,
    PR_SOURCE_ID,
    PR_SOURCE_URL,
    hasPrReunionLayer,
} from './pr-layer-configuration';
import type { ValidOrganizationNetworkData } from '../../../core/networkMap/ExternalNetworkMapPort';
import useFilterOnSessions from './hooks/use-filter-on-sessions';
import { MapControls } from './MapControls';
import { MapNavigation } from './MapNavigation';
import { NetworkMapDataLoading } from './NetworkMapDataLoading';
import { RefreshAllSessionsGradeLoading } from './RefreshAllSessionsGradeLoading';
import useQueryParams from '../../../utils/use-query-params';
import useStoreMapBounds from './hooks/use-store-map-bounds';
import { useRenderedSessionsIds } from './hooks/use-rendered-sessions-ids';
import { useRenderedSessionsIdsAfterInitialLoad } from './hooks/use-rendered-sessions-ids-after-initial-load';
import {
    LAYER_LINE_NETWORK_ROAD_DATA_ID,
    LAYER_FILL_DEGRADATIONS_ID,
    DEGRADATIONS_FILL_LAYER,
    ROAD_DATA_LINESTRING_LAYER,
} from './network-map-road-data-layer-configuration';
import RoadDataPopupOnClick from './MapPopup/RoadDataPopup/PopupOnClick';
import RoadDataPopupOnHover from './MapPopup/RoadDataPopup/PopupOnHover';
import DegradationPopupOnClick from './MapPopup/DegradationPopup/PopupOnClick';
import DegradationPopupOnHover from './MapPopup/DegradationPopup/PopupOnHover';

import { getSessionLayerConfig, SOURCE_NETWORK_SESSION_ID } from './network-map-session-layer-configuration';
import pinIcon from '../../../assets/icons/pin.svg';
import featureManager from './feature-manager';
import Image from './Image';
import {
    DEGRADATIONS_SOURCE_ID,
    DegradationProperties,
    IMAGES_SOURCE_ID,
    ImageProperties,
    ROADS_SOURCE_ID,
    WIDTHS_SOURCE_ID,
    WidthProperties,
} from '../../../core/domain/map';
import { useConfig } from '../../config';
import { SOURCES } from '../../../providers/map';
import useZoomControl from './hooks/use-zoom-control';
import MiniImagePiP from './MiniImagePiP';
import type { Expression } from 'mapbox-gl';
import { FIT_BOUNDS_OPTIONS, MAP_GRADE_COLORS } from './map-configuration';
import { theme } from '../../legacy/style/theme';
import { COLORS } from '../../legacy/components/StreetView/NavigationBar/Itinerary/use-itinerary';

const HIGHLIGHT_COLOR: Expression = [
    'case',
    ['get', 'hasState'],
    ['case', ['==', ['typeof', ['get', 'overallRate']], 'number'], ['step', ['get', 'overallRate'], ...MAP_GRADE_COLORS], COLORS.TARGET],
    theme.color.blue.primary,
];

const computeBearing = (from: [number, number], to: [number, number]): number => {
    const toRad = (d: number) => (d * Math.PI) / 180;
    const [lng1, lat1] = [toRad(from[0]), toRad(from[1])];
    const [lng2, lat2] = [toRad(to[0]), toRad(to[1])];
    const dLng = lng2 - lng1;
    const y = Math.sin(dLng) * Math.cos(lat2);
    const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
    return (Math.atan2(y, x) * (180 / Math.PI) + 360) % 360;
};

const DirectionMarker: FC<{ bearing: number }> = ({ bearing }) => (
    <svg
        width="44"
        height="44"
        viewBox="0 0 28 28"
        style={{ transform: `rotate(${bearing}deg)`, display: 'block' }}
    >
        <circle cx="14" cy="14" r="13" fill="#1D3875" stroke="white" strokeWidth="2" />
        <polygon points="14,3 20,17 14,13 8,17" fill="white" />
    </svg>
);

const ClickedPopup = MapPopup;
const HoverPopup = MapPopup;

// Layout's <Sidebar> is a fixed 62px — subtracted from window width to approximate this
// map's actual available width before it has mounted (see explicitViewState below).
const SIDEBAR_WIDTH_PX = 62;

const NetworkMap: FC = () => {
    const { debug, backendUrl, mapboxToken } = useConfig();
    const routeQueryParams: URLSearchParams = useQueryParams();
    const mapRef = useRef<MapRef | null>(null);
    const networkData = useAppSelector(networkMapQuery);
    const [mapIsLoaded, setMapIsLoaded] = useState<boolean>(false);
    const mapStyle = useAppSelector(selectedmapStyleQuery);
    const displayPr = useAppSelector(displayPrQuery);
    const displayRoads = useAppSelector(displayRoadsQuery);
    const displayDegradations = useAppSelector(displayDegradationsQuery);
    const highlightedRoadSectionLine = useAppSelector(highlightedRoadSectionLineQuery);
    const highlightedRoadSectionGeoJSON = useMemo(
        () =>
            highlightedRoadSectionLine
                ? {
                      type: 'Feature' as const,
                      properties: {
                          hasState: highlightedRoadSectionLine.overallRate !== undefined,
                          overallRate: highlightedRoadSectionLine.overallRate ?? null,
                      },
                      geometry: { type: 'LineString' as const, coordinates: highlightedRoadSectionLine.line },
                  }
                : null,
        [highlightedRoadSectionLine],
    );
    const updateRenderedSessionsIds = useRenderedSessionsIds(mapRef);
    const selectedGradeReference = useAppSelector(selectedGradeReferenceQuery);
    const updateRenderedSessionsIdsAfterInitialSourceDataLoad = useRenderedSessionsIdsAfterInitialLoad({
        updateRenderedSessionsIds,
    });

    const initialBounds = useAppSelector(initialBoundsQuery(routeQueryParams.get('sessionId')));
    // When opened from the mini map's expand button, lat/lng/zoom carry the exact view the
    // user was looking at there — use that instead of fitting the whole session's bbox.
    const explicitLat = routeQueryParams.get('lat');
    const explicitLng = routeQueryParams.get('lng');
    const explicitZoom = routeQueryParams.get('zoom');
    const explicitSourceWidth = routeQueryParams.get('w');
    const explicitViewState =
        explicitLat !== null && explicitLng !== null && explicitZoom !== null
            ? {
                  latitude: parseFloat(explicitLat),
                  longitude: parseFloat(explicitLng),
                  // The mini map is a small box; this map fills most of the viewport. At the
                  // same zoom number a wider viewport shows proportionally more surrounding
                  // area, which reads as "zoomed out" even though the number matches. Bump
                  // the zoom by the width ratio (in doublings) so the framing feels
                  // equivalent — computed upfront from window width (not the map container,
                  // which isn't mounted yet) so the very first paint is already correct and
                  // there's no visible snap once the map loads.
                  zoom:
                      parseFloat(explicitZoom) +
                      (explicitSourceWidth
                          ? Math.log2((window.innerWidth - SIDEBAR_WIDTH_PX) / parseFloat(explicitSourceWidth))
                          : 0),
              }
            : null;

    useZoomControl(mapRef);

    const layerFilters = useFilterOnSessions();
    const storeMapBounds = useStoreMapBounds();
    const fm = featureManager(mapRef, [LAYER_NETWORK_DATA_ID, LAYER_LINE_NETWORK_ROAD_DATA_ID, LAYER_FILL_DEGRADATIONS_ID]);
    const hoverImagePopup = useCreatePopupHover<ImageProperties>(
        fm,
        LAYER_NETWORK_DATA_ID,
        IMAGES_SOURCE_ID,
        IMAGES_SOURCE_ID,
        'none',
    );

    const [hoverBearing, setHoverBearing] = useState(0);

    useEffect(() => {
        const props = hoverImagePopup.networkProperties;
        if (!props || !mapRef.current || !mapIsLoaded) {
            setHoverBearing(0);
            return;
        }
        const features = mapRef.current.getMap().queryRenderedFeatures(undefined, { layers: [LAYER_NETWORK_DATA_ID] });

        const coordsOf = (imageIndex: number): [number, number] | null => {
            const f = features.find(
                (feat) => feat.properties?.sessionId === props.sessionId && feat.properties?.imageIndex === imageIndex,
            );
            if (!f || f.geometry.type !== 'Point') return null;
            return f.geometry.coordinates as [number, number];
        };

        const current = coordsOf(props.imageIndex);
        const next = coordsOf(props.imageIndex + 1);
        const prev = coordsOf(props.imageIndex - 1);

        if (current && next) {
            setHoverBearing(computeBearing(current, next));
        } else if (current && prev) {
            setHoverBearing(computeBearing(prev, current));
        } else {
            setHoverBearing(0);
        }
    }, [hoverImagePopup.networkProperties, mapIsLoaded]);

    const clickImagePopup = useCreatePopupClicked<ImageProperties>(
        fm,
        LAYER_NETWORK_DATA_ID,
        IMAGES_SOURCE_ID,
        IMAGES_SOURCE_ID,
    );
    const hoverRoadDataPopup = useCreatePopupHover<WidthProperties>(
        fm,
        LAYER_LINE_NETWORK_ROAD_DATA_ID,
        WIDTHS_SOURCE_ID,
        WIDTHS_SOURCE_ID,
    );
    const clickRoadDataPopup = useCreatePopupClicked<WidthProperties>(
        fm,
        LAYER_LINE_NETWORK_ROAD_DATA_ID,
        WIDTHS_SOURCE_ID,
        WIDTHS_SOURCE_ID,
    );
    const hoverDegradationPopup = useCreatePopupHover<DegradationProperties>(
        fm,
        LAYER_FILL_DEGRADATIONS_ID,
        DEGRADATIONS_SOURCE_ID,
        DEGRADATIONS_SOURCE_ID,
    );
    const clickDegradationPopup = useCreatePopupClicked<DegradationProperties>(
        fm,
        LAYER_FILL_DEGRADATIONS_ID,
        DEGRADATIONS_SOURCE_ID,
        DEGRADATIONS_SOURCE_ID,
    );

    const initializeMap = () => {
        const map = mapRef?.current?.getMap();
        if (map) {
            setMapIsLoaded(true);
            // reuseMaps hands back an already-existing instance still parked at whatever
            // camera it last had — initialViewState only applies the very first time an
            // instance is created, so re-apply the requested position/zoom explicitly here.
            // (explicitViewState.zoom is already width-compensated — see its definition.)
            if (explicitViewState) {
                map.jumpTo({
                    center: [explicitViewState.longitude, explicitViewState.latitude],
                    zoom: explicitViewState.zoom,
                });
            } else if (highlightedRoadSectionLine) {
                const lngs = highlightedRoadSectionLine.line.map(([lng]) => lng);
                const lats = highlightedRoadSectionLine.line.map(([, lat]) => lat);
                map.fitBounds(
                    [Math.min(...lngs), Math.min(...lats), Math.max(...lngs), Math.max(...lats)],
                    FIT_BOUNDS_OPTIONS,
                );
            }
        }
    };

    const handleOnMouseMoveOnMap = (event: MapLayerMouseEvent) => {
        hoverImagePopup.popupOnHover(event);
        hoverRoadDataPopup.popupOnHover(event);
        hoverDegradationPopup.popupOnHover(event);
    };

    const handleOnClickOnMap = (event: MapLayerMouseEvent) => {
        clickImagePopup.popupOnClick(event);
        clickRoadDataPopup.popupOnClick(event);
        clickDegradationPopup.popupOnClick(event);
    };

    const handleMapLoad = async (event: MapboxEvent) => {
        if (event.type === 'load') {
            initializeMap();
        }
    };

    const handleMapIdle = () => {
        if (!mapIsLoaded) {
            // restore grades on map reuse
            initializeMap();
        }
    };

    const handleMoveEnd = () => {
        if (mapIsLoaded) {
            storeMapBounds(mapRef);
        }
    };

    const handleZoomIn = () => {
        const map = mapRef.current;
        if (map) {
            map.zoomIn();
        }
    };

    const handleZoomOut = () => {
        const map = mapRef.current;
        if (map) {
            map.zoomOut();
        }
    };

    const getSessionNetworkMapData = (): ValidOrganizationNetworkData | undefined => {
        if (!networkData?.sessionsData) {
            return {
                type: 'FeatureCollection',
                features: [],
            };
        }

        return networkData?.sessionsData as ValidOrganizationNetworkData;
    };

    const transformRequest: TransformRequestFunction = useCallback(
        (url) => {
            if (url.startsWith(backendUrl!)) {
                return {
                    url,
                    credentials: 'include',
                };
            }

            return {
                url,
            };
        },
        [backendUrl],
    );

    useEffect(() => {
        if (mapRef.current) {
            mapRef.current.getMap().showTileBoundaries = debug;
        }
    }, [debug, mapRef]);

    if (!networkData) {
        return <NetworkMapDataLoading />;
    }

    return (
        <>
            <MapboxGlMap
                reuseMaps
                ref={mapRef}
                mapboxAccessToken={mapboxToken}
                onLoad={handleMapLoad}
                onIdle={handleMapIdle}
                onMoveEnd={handleMoveEnd}
                attributionControl={false}
                mapStyle={
                    mapStyle === 'STREETS'
                        ? 'mapbox://styles/mapbox/streets-v9'
                        : 'mapbox://styles/mapbox/satellite-streets-v12'
                }
                style={{ width: '100%', position: 'absolute', top: 0, bottom: 0 }}
                onClick={handleOnClickOnMap}
                onSourceData={updateRenderedSessionsIdsAfterInitialSourceDataLoad}
                onMouseMove={handleOnMouseMoveOnMap}
                initialViewState={
                    explicitViewState ?? {
                        bounds: initialBounds ?? undefined,
                        fitBoundsOptions: {
                            animate: false,
                            padding: {
                                top: 100,
                                left: 100,
                                right: 100,
                                bottom: 100,
                            },
                        },
                    }
                }
                transformRequest={transformRequest}
            >
                <Image name="pin" src={pinIcon} height={50} width={50} sdf />
                <Source id={SOURCE_NETWORK_SESSION_ID} type="geojson" data={getSessionNetworkMapData()} generateId />
                {Object.entries(SOURCES).map(([sourceId, { tilesPath, minZoom, maxZoom }]) => (
                    <Source
                        key={sourceId}
                        id={sourceId}
                        type="vector"
                        tiles={[`${backendUrl}${tilesPath}`]}
                        minzoom={minZoom}
                        maxzoom={maxZoom}
                        promoteId="id"
                    />
                ))}
                <Layer
                    id="roads"
                    source={ROADS_SOURCE_ID}
                    source-layer={ROADS_SOURCE_ID}
                    type="line"
                    layout={{ visibility: displayRoads ? 'visible' : 'none' }}
                    paint={{ 'line-color': 'rgb(0,0,0)', 'line-width': 3 }}
                />
                {highlightedRoadSectionGeoJSON && (
                    // Drawn right above the base "roads" layer, below the ZH/grade, degradation
                    // and image layers, so it marks the section without hiding pavement-condition
                    // data drawn on top of it — a wide, translucent glow rather than a solid cover.
                    <Source id="highlighted-road-section" type="geojson" data={highlightedRoadSectionGeoJSON}>
                        <Layer
                            id="highlighted-road-section-line"
                            type="line"
                            paint={{
                                'line-color': HIGHLIGHT_COLOR,
                                'line-width': 10,
                                'line-opacity': 0.45,
                            }}
                        />
                    </Source>
                )}
                <Layer
                    {...getSessionLayerConfig(selectedGradeReference)}
                    source={SOURCE_NETWORK_SESSION_ID}
                    filter={layerFilters.sessions}
                />
                <Layer
                    {...DEGRADATIONS_FILL_LAYER}
                    source={DEGRADATIONS_SOURCE_ID}
                    source-layer={DEGRADATIONS_SOURCE_ID}
                    layout={{ visibility: displayDegradations ? 'visible' : 'none' }}
                    filter={layerFilters.roadDatas}
                />
                <Layer
                    {...ROAD_DATA_LINESTRING_LAYER}
                    source={WIDTHS_SOURCE_ID}
                    source-layer={WIDTHS_SOURCE_ID}
                    filter={layerFilters.roadDatas}
                />
                <Layer
                    {...getLayerConfig(selectedGradeReference)}
                    source={IMAGES_SOURCE_ID}
                    source-layer={IMAGES_SOURCE_ID}
                    filter={layerFilters.sessionImages}
                />
                <Source id={PR_SOURCE_ID} type="vector" url={PR_SOURCE_URL}>
                    <Layer
                        {...PR_LAYER}
                        layout={{ ...PR_LAYER.layout, visibility: displayPr ? 'visible' : 'none' }}
                        paint={mapStyle === 'STREETS' ? PR_LAYER_PAINT_STREET : PR_LAYER_PAINT_SATELLITE}
                    />
                </Source>
                {hasPrReunionLayer && (
                    <Source id={PR_REUNION_SOURCE_ID} type="vector" url={PR_REUNION_SOURCE_URL}>
                        <Layer
                            {...PR_REUNION_LAYER}
                            layout={{ ...PR_REUNION_LAYER.layout, visibility: displayPr ? 'visible' : 'none' }}
                            paint={mapStyle === 'STREETS' ? PR_LAYER_PAINT_STREET : PR_LAYER_PAINT_SATELLITE}
                        />
                    </Source>
                )}
                {hoverImagePopup.popupIsHovered && hoverImagePopup.popupCoordinates && (
                    <Marker
                        latitude={hoverImagePopup.popupCoordinates[0]}
                        longitude={hoverImagePopup.popupCoordinates[1]}
                    >
                        <div style={{ cursor: 'none' }}>
                            <DirectionMarker bearing={hoverBearing} />
                        </div>
                    </Marker>
                )}
                {/* popups for images */}
                <ClickedPopup
                    closeButton
                    popupIsShown={clickImagePopup.popupIsClicked}
                    popupCoordinates={clickImagePopup.popupCoordinates as [number, number]}
                    hidePopup={clickImagePopup.hideClickedPopup}
                >
                    <ImagePopupOnClick properties={clickImagePopup.networkProperties} />
                </ClickedPopup>
                <HoverPopup
                    closeButton={false}
                    popupIsShown={hoverImagePopup.popupIsHovered}
                    popupCoordinates={hoverImagePopup.popupCoordinates as [number, number]}
                    hidePopup={hoverImagePopup.hideHoverPopup}
                    offset={50}
                >
                    <ImagePopupOnHover properties={hoverImagePopup.networkProperties} />
                </HoverPopup>
                {/* popups for road-datas */}
                <ClickedPopup
                    closeButton
                    popupIsShown={clickRoadDataPopup.popupIsClicked}
                    popupCoordinates={clickRoadDataPopup.popupCoordinates as [number, number]}
                    hidePopup={clickRoadDataPopup.hideClickedPopup}
                >
                    <RoadDataPopupOnClick networkProperties={clickRoadDataPopup.networkProperties} />
                </ClickedPopup>
                <HoverPopup
                    closeButton={false}
                    popupIsShown={hoverRoadDataPopup.popupIsHovered}
                    popupCoordinates={hoverRoadDataPopup.popupCoordinates as [number, number]}
                    hidePopup={hoverRoadDataPopup.hideHoverPopup}
                >
                    <RoadDataPopupOnHover networkProperties={hoverRoadDataPopup.networkProperties} />
                </HoverPopup>
                {/* popups for degradations */}
                <ClickedPopup
                    closeButton
                    popupIsShown={clickDegradationPopup.popupIsClicked}
                    popupCoordinates={clickDegradationPopup.popupCoordinates as [number, number]}
                    hidePopup={clickDegradationPopup.hideClickedPopup}
                >
                    <DegradationPopupOnClick networkProperties={clickDegradationPopup.networkProperties} />
                </ClickedPopup>
                <HoverPopup
                    closeButton={false}
                    popupIsShown={hoverDegradationPopup.popupIsHovered}
                    popupCoordinates={hoverDegradationPopup.popupCoordinates as [number, number]}
                    hidePopup={hoverDegradationPopup.hideHoverPopup}
                >
                    <DegradationPopupOnHover networkProperties={hoverDegradationPopup.networkProperties} />
                </HoverPopup>
            </MapboxGlMap>
            <MapNavigation />
            <MapControls handleZoomIn={handleZoomIn} handleZoomOut={handleZoomOut} />
            <MiniImagePiP properties={hoverImagePopup.networkProperties} />
            <RefreshAllSessionsGradeLoading />
            {clickImagePopup.networkProperties && (
                <EnlargedImageModal networkProperties={clickImagePopup.networkProperties} />
            )}
            {clickRoadDataPopup.networkProperties && (
                <EnlargedImageModal networkProperties={clickRoadDataPopup.networkProperties} />
            )}
            {clickDegradationPopup.networkProperties?.imageIndex != null &&
                clickDegradationPopup.networkProperties && (
                    <EnlargedImageModal networkProperties={clickDegradationPopup.networkProperties} />
                )}
        </>
    );
};

export default NetworkMap;
